import * as React from "react";
import {makeStyles, Theme, useTheme} from '@material-ui/core/styles';
import {useTranslation} from 'react-i18next';
import {IData as AutomaticTaskListData} from '../../../store/automatictask/AutomatictaskList';
import {DopTable, IColumnDefinition, RowState} from '../../organisms/DopTable';
import {DetailsButton} from '../../atoms/buttons/DetailsButton';
import {EditButton} from '../../atoms/buttons/EditButton';
import { Chip } from '@material-ui/core';

// All the incoming values
interface IProps {
    title: string;
    data: AutomaticTaskListData[];
    loading: boolean;
    hasError: boolean;
    errorDetails: string;
}

// Outgoing functions
interface IDispatch {
    onClickDetails: (id: string) => void;
    onClickEdit: (id: string) => void;
    onRefresh: () => void;
}

// CSS
const useStyles = makeStyles((theme: Theme) => ({ 
    adminChip: {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
    },
    requiredChip: {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.contrastText,
    }
}));

// Actual Component
export type AllProps = IProps & IDispatch;
export const AutomaticTaskList = (props: AllProps) => {
    const classes = useStyles(useTheme());
    const { t: tCommon } = useTranslation('common');
    const { t: tDatatypes } = useTranslation('datatypes');
    const { t: tAutomaticTask } = useTranslation('automatictask');

    const columns: IColumnDefinition<AutomaticTaskListData>[] = [
        { 
            title: tDatatypes('titleLabel'), 
            align: "left", 
            renderer: (value) => value.title,
            sorter: (a, b) => a.title.localeCompare(b.title),
            searchable: true
        },
        { 
            title: tDatatypes('beschrijvingLabel'), 
            align: "left", 
            renderer: (value) => value.description || '-',
            sorter: (a, b) => (a.description || '').localeCompare(b.description || ''),
            searchable: true
        },
        { 
            title: tDatatypes('statusLabel'), 
            align: "left",
            renderer: (value) => value.statusName || '-',
            sorter: (a, b) => (a.statusName || '').localeCompare(b.statusName || ''),
            searchable: true
        },
        { 
            title: tDatatypes('aantalDagenLabel'), 
            align: "center", 
            renderer: (value) => value.numberOfDays.toString(),
            sorter: (a, b) => a.numberOfDays - b.numberOfDays,
            searchable: false
        },
        {
            title: tDatatypes('adminLabel'),
            align: "center",
            renderer: (value) => value.admin ? (
                <Chip 
                    size="small" 
                    label="Admin" 
                    className={classes.adminChip}
                />
            ) : null,
            sorter: (a, b) => Number(b.admin) - Number(a.admin),
            searchable: false
        },
        {
            title: tDatatypes('verplichtLabel'),
            align: "center",
            renderer: (value) => value.required ? (
                <Chip 
                    size="small" 
                    label="Verplicht" 
                    className={classes.requiredChip}
                />
            ) : null,
            sorter: (a, b) => Number(b.required) - Number(a.required),
            searchable: false
        },
        { 
            title: tCommon('actions'), 
            align: "right", 
            renderer: (value) => (
                <>
                    <DetailsButton onClick={() => props.onClickDetails(value.streamId)} />
                    <EditButton onClick={() => props.onClickEdit(value.streamId)} />
                </>
            ),
            searchable: false // Don't search in action buttons
        },
    ]

    // Map automatic task data - all tasks are active by default since there's no active field
    const rowsWithStatus = props.data.map(task => ({
        ...task,
        status: 'normal' as const
    }));

    return (<DopTable 
                data={{
                    rows: rowsWithStatus,
                    columns: columns,
                }}
                toolbar={{
                    title: props.title,
                    onRefresh: props.onRefresh,
                    enableBuiltinSearch: true,
                    searchPlaceholder: tAutomaticTask('searchPlaceholder')
                }}
                loading={props.loading}
                hasError={props.hasError}
                errorDetails={props.errorDetails}
                onClickRow={(row) => props.onClickDetails(row.streamId)}
                getRowState={(row) => RowState.Normal} // All tasks are normal state
            />)
}
